import cn from 'classnames';
import parse from 'html-react-parser';
import { FC } from 'react';

import { Button } from '@catawiki/cata-style';

import {
  SanitizeMode,
  getCleanInput,
  isBuilderStringPresent,
  someBuilderStringPresent,
} from '@/shared/text';
import { BuilderString, CtaVariant } from '@/shared/types';
import { IconName } from '@/shared/constants/icons';

import { FlatMediaInput, Media, MediaType } from '../Media';
import styles from './Topic.module.scss';
import { TopicLead } from './TopicLead';

export interface TopicRow extends FlatMediaInput {
  number?: BuilderString;
  icon?: IconName;
  title: BuilderString;
  subtitle: BuilderString;
  description: BuilderString;
  ctaText?: BuilderString;
  ctaUrl?: BuilderString;
}

interface Props extends TopicRow {
  asBox: boolean;
  backgroundColorClassName: string;
  ctaVariant: CtaVariant;
  className?: string;
  locale: string;
}

const TEXT_SPACING = 'u-m-t-sm-m u-m-t-md-m u-m-t-lg-xl';

export const Topic: FC<Props> = ({
  title,
  subtitle,
  description,
  ctaUrl,
  ctaVariant,
  ctaText,
  backgroundColorClassName,
  asBox = false,
  icon,
  number,
  locale,
  className,

  mediaType,
  mediaCaption,
  imageUrl,
  imageLink,
  imageLinkTarget,
  videoUrl,
  videoAutoPlay,
  youtubeUrl,
  youtubeDisabledDialog,
}) => (
  <section className={className}>
    <div
      className={cn(backgroundColorClassName, {
        [styles.container]: true,
        'u-p-xl u-p-lg-xxl-4': asBox,
      })}
    >
      <Media
        className={styles.imageContainer}
        containerClassName={styles.image}
        locale={locale}
        mediaType={mediaType}
        mediaCaption={mediaCaption}
        image={imageUrl}
        imageLink={imageLink}
        imageLinkTarget={imageLinkTarget}
        video={{
          source: videoUrl || '',
          autoPlay: videoAutoPlay,
          poster: imageUrl,
        }}
        youtube={{
          url: youtubeUrl || '',
          poster: imageUrl || '',
          disabledDialog: youtubeDisabledDialog,
        }}
      />
      <div className={cn({ 'u-m-t-xl': mediaType !== MediaType.NONE })}>
        <TopicLead icon={icon} number={number} locale={locale} />
        {isBuilderStringPresent(title, locale) ? (
          <div
            className={cn('u-typography-h3', {
              [TEXT_SPACING]: someBuilderStringPresent([number], locale),
            })}
          >
            {parse(getCleanInput(locale, SanitizeMode.SINGLE_LINE, title))}
          </div>
        ) : null}
        {isBuilderStringPresent(subtitle, locale) ? (
          <div
            className={cn('u-typography-h6', 'u-color-secondary', {
              [TEXT_SPACING]: someBuilderStringPresent([number, title], locale),
            })}
          >
            {parse(getCleanInput(locale, SanitizeMode.SINGLE_LINE, subtitle))}
          </div>
        ) : null}
        {isBuilderStringPresent(description, locale) ? (
          <div
            className={cn('u-color-secondary', 'u-typography-h7', {
              [TEXT_SPACING]: someBuilderStringPresent(
                [number, title, subtitle],
                locale
              ),
            })}
          >
            {parse(
              getCleanInput(locale, SanitizeMode.SINGLE_LINE, description)
            )}
          </div>
        ) : null}
        {ctaVariant === 'none' ? null : (
          <Button
            className={cn({
              [TEXT_SPACING]: someBuilderStringPresent(
                [number, title, subtitle, description],
                locale
              ),
            })}
            variant={ctaVariant}
            as="a"
            href={getCleanInput(
              locale,
              SanitizeMode.SINGLE_LINE_TEXT_ONLY,
              ctaUrl
            )}
          >
            {getCleanInput(locale, SanitizeMode.SINGLE_LINE_TEXT_ONLY, ctaText)}
          </Button>
        )}
      </div>
    </div>
  </section>
);
